import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import InputField from '../../components/ui/InputField';
import Dialog from '../../components/ui/Dialog';
import ContentCopyIcon from '../../components/icons/ContentCopyIcon';
import TrashIcon from '../../components/icons/TrashIcon';
import PlayArrowIcon from '../../components/icons/PlayArrowIcon';
import RedoIcon from '../../components/icons/RedoIcon';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../utils/firebase';
import { collection, addDoc, doc, updateDoc, onSnapshot, deleteDoc, serverTimestamp, arrayRemove } from 'firebase/firestore';
import { MafiaGameRoom, MafiaPlayer, MafiaRole } from '../../types/game';
import { isTeacher } from '../../utils/firebase';

const getRoleName = (role: MafiaRole) => {
  const roleNames: Record<MafiaRole, string> = {
    mafia: '마피아', citizen: '시민', police: '경찰', doctor: '의사', detective: '탐정'
  };
  return roleNames[role];
};

const MafiaGameCreate: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [roomName, setRoomName] = useState('');
  const [gameCode, setGameCode] = useState('');
  const [gameRoom, setGameRoom] = useState<MafiaGameRoom | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [roles, setRoles] = useState({ mafia: 2, citizen: 4, police: 1, doctor: 1, detective: 1 });

  useEffect(() => {
    if (currentUser && !isTeacher(currentUser)) navigate('/tools');
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!gameRoom?.id) return;
    const unsub = onSnapshot(doc(db, 'mafiaGames', gameRoom.id), (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data() as any;
        setGameRoom({
          id: snapshot.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt || new Date()),
          players: (data.players || []).map((p: any) => ({
            ...p,
            joinedAt: p.joinedAt?.toDate ? p.joinedAt.toDate() : (p.joinedAt || new Date())
          }))
        } as MafiaGameRoom);
      }
    });
    return () => unsub();
  }, [gameRoom?.id]);

  const createGameRoom = async () => {
    if (!currentUser || !isTeacher(currentUser)) {
      setError('교사만 게임 방을 생성할 수 있습니다.');
      return;
    }
    if (!roomName.trim()) {
      setError('방 이름을 입력해주세요.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const code = Math.random().toString(36).substring(2, 8).toUpperCase();
      const newRoom: Omit<MafiaGameRoom, 'id'> = {
        code, teacherId: currentUser.uid, teacherName: currentUser.name,
        players: [], status: 'waiting', createdAt: new Date(), roles
      };
      const docRef = await addDoc(collection(db, 'mafiaGames'), { ...newRoom, createdAt: serverTimestamp() });
      setGameCode(code);
      setGameRoom({ ...newRoom, id: docRef.id } as MafiaGameRoom);
    } catch (err: any) {
      setError('게임 방 생성에 실패했습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const assignRoles = async () => {
    if (!gameRoom) return;
    const totalRoles = Object.values(roles).reduce((sum, count) => sum + count, 0);
    if (gameRoom.players.length < totalRoles) {
      setError(`최소 ${totalRoles}명의 플레이어가 필요합니다.`);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const roleList: MafiaRole[] = [];
      Object.entries(roles).forEach(([role, count]) => {
        for (let i = 0; i < count; i++) roleList.push(role as MafiaRole);
      });
      const shuffledRoles = [...roleList].sort(() => Math.random() - 0.5);
      const updatedPlayers = gameRoom.players.map((player, index) => ({
        ...player,
        role: shuffledRoles[index],
        isAlive: true,
        isReady: false
      }));
      await updateDoc(doc(db, 'mafiaGames', gameRoom.id), { players: updatedPlayers, status: 'role-assigned', roles });
      setRoleDialogOpen(false);
    } catch (err: any) {
      setError('역할 배정에 실패했습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteGameRoom = async () => {
    if (!gameRoom) return;
    if (window.confirm('게임 방을 삭제하시겠습니까?')) {
      try {
        await deleteDoc(doc(db, 'mafiaGames', gameRoom.id));
        setGameRoom(null);
        setGameCode('');
        setRoomName('');
      } catch (err: any) {
        setError('게임 방 삭제에 실패했습니다: ' + err.message);
      }
    }
  };

  const copyCode = () => {
    if (gameCode) {
      navigator.clipboard.writeText(gameCode);
      alert('초대 코드가 복사되었습니다!');
    }
  };

  const removePlayer = async (playerId: string) => {
    if (!gameRoom) return;
    try {
      const playerToRemove = gameRoom.players.find(p => p.id === playerId);
      if (playerToRemove) {
        await updateDoc(doc(db, 'mafiaGames', gameRoom.id), { players: arrayRemove(playerToRemove) });
      }
    } catch (err: any) {
      setError('플레이어 제거에 실패했습니다: ' + err.message);
    }
  };

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 24px' }}>
      <h1 style={{ marginBottom: 16, fontSize: 24, fontWeight: 700 }}>마피아 게임 방 생성</h1>
      {error && (
        <div style={{ marginBottom: 16, padding: 12, background: '#ffebee', borderRadius: 8, color: '#c62828' }}>
          {error}
          <button type="button" onClick={() => setError(null)} style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        </div>
      )}

      {!gameRoom ? (
        <Card padding="32px">
          <InputField label="방 이름" value={roomName} onChange={setRoomName} placeholder="예: 3학년 1반 마피아 게임" />
          <h2 style={{ marginTop: 24, marginBottom: 16, fontSize: 18 }}>역할 설정</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 16, marginBottom: 24 }}>
            {Object.entries(roles).map(([role, count]) => (
              <Card key={role} padding="12px">
                <p style={{ margin: '0 0 8px', fontSize: 12, color: '#666' }}>{getRoleName(role as MafiaRole)}</p>
                <input
                  type="number"
                  min={0}
                  value={count}
                  onChange={(e) => {
                    const newCount = parseInt(e.target.value) || 0;
                    if (newCount >= 0) setRoles({ ...roles, [role]: newCount });
                  }}
                  style={{ width: '100%', padding: 8 }}
                />
              </Card>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
            <Button variant="outline" onClick={() => navigate('/tools')}>취소</Button>
            <Button variant="primary" onClick={createGameRoom} disabled={loading} leftIcon={<PlayArrowIcon width={20} height={20} />}>
              {loading ? '생성 중...' : '게임 방 생성'}
            </Button>
          </div>
        </Card>
      ) : (
        <Card padding="32px">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h2 style={{ margin: 0, fontSize: 20 }}>{roomName || '게임 방'}</h2>
            <Button variant="outline" onClick={deleteGameRoom} leftIcon={<TrashIcon width={18} height={18} />}>방 삭제</Button>
          </div>
          <div style={{ marginBottom: 24, padding: 16, backgroundColor: '#e3f2fd', borderRadius: 8 }}>
            <p style={{ margin: '0 0 8px', fontSize: 14, color: '#666' }}>초대 코드</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 24, fontWeight: 700, fontFamily: 'monospace' }}>{gameCode}</span>
              <button type="button" onClick={copyCode} style={{ padding: 4 }}><ContentCopyIcon width={20} height={20} /></button>
            </div>
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#666' }}>학생들에게 이 코드를 공유하세요</p>
          </div>
          <h3 style={{ marginBottom: 12, fontSize: 16 }}>참가자 ({gameRoom.players.length}명)</h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px' }}>
            {gameRoom.players.length === 0 ? (
              <li style={{ padding: 12, color: '#666' }}>아직 참가자가 없습니다</li>
            ) : (
              gameRoom.players.map((player) => (
                <li key={player.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                  <span>{player.name} {player.role ? `· ${getRoleName(player.role)}` : '· 역할 미배정'}</span>
                  <button type="button" onClick={() => removePlayer(player.id)} style={{ padding: 4 }}><TrashIcon width={18} height={18} /></button>
                </li>
              ))
            )}
          </ul>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
            <Button variant="outline" onClick={() => setRoleDialogOpen(true)} disabled={gameRoom.players.length === 0 || gameRoom.status === 'role-assigned'} leftIcon={<RedoIcon width={18} height={18} />}>역할 재배정</Button>
            <Button variant="primary" onClick={() => setRoleDialogOpen(true)} disabled={gameRoom.players.length === 0 || gameRoom.status === 'role-assigned'} leftIcon={<PlayArrowIcon width={18} height={18} />}>역할 배정하기</Button>
          </div>
          {gameRoom.status === 'role-assigned' && (
            <div style={{ marginTop: 16, padding: 12, background: '#e8f5e9', borderRadius: 8, color: '#2e7d32' }}>
              역할이 배정되었습니다! 학생들이 자신의 역할을 확인할 수 있습니다.
            </div>
          )}
        </Card>
      )}

      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} title="역할 배정" actions={
        <>
          <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>취소</Button>
          <Button variant="primary" onClick={assignRoles} disabled={loading}>{loading ? '배정 중...' : '배정하기'}</Button>
        </>
      }>
        <p>총 {Object.values(roles).reduce((sum, count) => sum + count, 0)}개의 역할을 {gameRoom?.players.length}명의 플레이어에게 배정합니다.</p>
        <p style={{ marginTop: 16, color: '#666', fontSize: 14 }}>역할은 랜덤으로 배정됩니다.</p>
      </Dialog>
    </div>
  );
};

export default MafiaGameCreate;
